import { BadgeEditValues, IPersonBadge } from "../../../../../common/types";
import { CustomerTypes } from "../../../../createBadge/enums/CustomerTypes";
import { BadgeStatuses } from "../../../../../common/enums";
import { hasUserAccessField } from "../../../../createBadge/utils/utils";
import BadgeHelper from "../../../../../common/badges/helper/BadgeHelper";

const getUpdatedBadge = (
  badge: IPersonBadge,
  selectedCustomerType: string,
  formValues: BadgeEditValues,
  skipBadgeVersionUpdate?: boolean
): IPersonBadge => {
  const {
    personInfo: { firstName, jobTitle, lastName, email, mobilePhone },
    supportStaff: { companyName, standsWorkingOn, address, reportsTo, userAccess, mobilePhone: onsiteMobileNumber },
    productCode
  } = formValues;

  const supportStaffDetails = {
    companyName,
    companyAddress: address,
    stands: standsWorkingOn,
    reportsTo,
    mobilePhone: onsiteMobileNumber,
    userAccess
  };

  const newBadgeVersion = badge.badgeVersion ? badge.badgeVersion + 1 : 1;
  return {
    ...badge,
    badgeVersion: skipBadgeVersionUpdate ? badge.badgeVersion : newBadgeVersion,
    customerType: selectedCustomerType,
    productCode,
    organisation: { ...badge.organisation, displayName: badge.organisation.displayName },
    contact: {
      ...badge.contact,
      firstName,
      lastName,
      jobTitle,
      email,
      mobilePhone
    },
    supportStaffDetails,
  status: badge.status
  };
};

const getUpdatedBadgeAfterUpgrade = (badge: IPersonBadge, newCustomerType: string, newBadgeProduct: string) => {
  return { ...badge, productCode: newBadgeProduct, customerType: newCustomerType };
}

const isBadgeNonExisting = (e: Error) => {
  try {
    const parsed = JSON.parse(e.message);
    return parsed && parsed.extensions && parsed.extensions.code === "notfound";
  } catch (error) {
    return false;
  }
};

const getBadgesWithReplacedItem = (badges: IPersonBadge[], updatedBadge: IPersonBadge) => {
  return badges.map(item => {
    if (item.id === updatedBadge.id) {
      item = updatedBadge;
    }
    return item;
  });
};

const isStaffOrContractor = (customerType: string) => {
  return (
    customerType === CustomerTypes.STAFF || customerType === CustomerTypes.CONTRACTOR
  );
};

const isBadgeInactive = (badge: IPersonBadge) => {
  return badge.status === BadgeStatuses.INACTIVE;
};

const isBadgeCancelled = (badge: IPersonBadge) => {
  return badge.status === BadgeStatuses.CANCELLED;
}

const areMainFieldValuesSame = (badge: IPersonBadge, firstNameValue: string, lastNameValue: string, jobTitleValue: string, selectedBadgeType: string) => {
  const { contact } = badge;
  return Boolean(firstNameValue === contact.firstName &&
    lastNameValue === contact.lastName &&
    jobTitleValue === contact.jobTitle  &&
    badge.productCode === selectedBadgeType)
}

const areFieldValuesSame = (
  badge: IPersonBadge,
  firstNameValue: string,
  lastNameValue: string,
  jobTitleValue: string,
  mobilePhoneValue: string,
  staffDetails: IPersonBadge["supportStaffDetails"],
  selectedCustomerType: string,
  selectedBadgeType: string
) => {
  const { contact } = badge;
  const areSupportDetailsSame = (hasUserAccessField(badge.customerType) ? JSON.stringify(badge.supportStaffDetails) === JSON.stringify(staffDetails) : true);

  return Boolean(
    areMainFieldValuesSame(badge, firstNameValue, lastNameValue, jobTitleValue, selectedBadgeType) &&
    mobilePhoneValue === contact.mobilePhone &&
    badge.customerType === selectedCustomerType &&
    areSupportDetailsSame
  );
};

const shouldUpdateBadgeVersion = (badge: IPersonBadge,
                                  firstNameValue: string,
                                  lastNameValue: string,
                                  jobTitleValue: string,
                                  selectedBadgeType: string,
                                  mobilePhoneValue: string) => {
  return !areMainFieldValuesSame(badge, firstNameValue, lastNameValue, jobTitleValue, selectedBadgeType) || mobilePhoneValue !== badge.contact.mobilePhone;
}

const getSupportStaffDetails = (values: BadgeEditValues) => {
  const {
    supportStaff: { companyName, standsWorkingOn, address, reportsTo, userAccess, mobilePhone: onsiteMobileNumber }
  } = values;
  return {
    companyName,
    companyAddress: address,
    stands: standsWorkingOn,
    reportsTo,
    mobilePhone: onsiteMobileNumber,
    userAccess
  };
};

const searchFormUtils = {
  getUpdatedBadge,
  isBadgeNonExisting,
  getBadgesWithReplacedItem,
  isStaffOrContractor,
  isBadgeInactive,
  areFieldValuesSame,
  isBadgeCancelled,
  shouldUpdateBadgeVersion,
  getUpdatedBadgeAfterUpgrade,
  getSupportStaffDetails
};

export default searchFormUtils;
